import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Layout } from '@/components/ui/Layout';
import { supabase } from '@/lib/supabase';
import { ClipboardList, Plus, Loader2, Copy, Eye } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { SurveyTemplate, SurveyQuestion, SurveyRespondent } from '@/types/database';
import { SurveyList } from '@/components/surveys/SurveyList';
import { SurveyFormModal } from '@/components/surveys/SurveyFormModal';
import { SendSurveyModal } from '@/components/surveys/SendSurveyModal';
import { CreateCampaignModal } from '@/components/surveys/CreateCampaignModal';
import { SurveyPreviewModal } from '@/components/surveys/SurveyPreviewModal';
import { SurveyFilters } from '@/components/surveys/SurveyFilters';
import { CampaignList, CampaignWithCounts } from '@/components/surveys/CampaignList';
import { sendSurveyEmail, sendCampaignEmail } from '@/lib/email';
import { ErrorDisplay } from '@/components/ui/ErrorDisplay';

interface SurveyTemplateWithQuestions extends SurveyTemplate {
  questions: SurveyQuestion[];
}

export function Surveys() {
  const { user } = useAuth();
  const { organizationId } = useOrganization();
  const [templates, setTemplates] = useState<SurveyTemplateWithQuestions[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<SurveyTemplateWithQuestions[]>([]);
  const [adminTemplates, setAdminTemplates] = useState<SurveyTemplateWithQuestions[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SurveyTemplateWithQuestions | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<SurveyTemplateWithQuestions | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    questions: [{ text: '', type: 'multiple-choice' as const, options: [''], order_index: 0 }]
  });
  const [saving, setSaving] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SurveyTemplate | null>(null);
  const [respondents, setRespondents] = useState<SurveyRespondent[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignRespondents, setCampaignRespondents] = useState<SurveyRespondent[]>([]);
  const [activeView, setActiveView] = useState<'templates' | 'campaigns'>('templates');
  const [campaigns, setCampaigns] = useState<CampaignWithCounts[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  const isAdmin = user?.email?.endsWith('@admin.com');

  useEffect(() => {
    loadTemplates();
  }, [organizationId, isAdmin]);

  useEffect(() => {
    if (templates.length > 0) {
      filterTemplates();
    }
  }, [templates, statusFilter]);

  const filterTemplates = () => {
    if (statusFilter === null) {
      setFilteredTemplates(templates);
    } else {
      setFilteredTemplates(templates.filter(template => template.status === statusFilter));
    }
  };

  const handleFilterChange = (status: string | null) => {
    setStatusFilter(status);
  };

  async function loadTemplates() {
    try {
      setLoading(true);
      setError(null);

      if (isAdmin) {
        const { data: templatesData, error: templatesError } = await supabase
          .from('survey_templates')
          .select(`
            *,
            questions:survey_questions(*)
          `)
          .is('organization_id', null)
          .order('created_at', { ascending: false });

        if (templatesError) {
          throw new Error(`Failed to load templates: ${templatesError.message}`, { cause: templatesError });
        }
        
        setTemplates([...(templatesData || [])]);
        setFilteredTemplates([...(templatesData || [])]);
        setAdminTemplates([]);
      } else {
        const [orgTemplates, adminTemplates] = await Promise.all([
          supabase
            .from('survey_templates')
            .select(`
              *,
              questions:survey_questions(*)
            `)
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false }),

          supabase
            .from('survey_templates')
            .select(`
              *,
              questions:survey_questions(*)
            `)
            .is('organization_id', null)
            .eq('status', 'published')
            .order('created_at', { ascending: false })
        ]);

        if (orgTemplates.error) {
          throw new Error(`Failed to load organization templates: ${orgTemplates.error.message}`, { cause: orgTemplates.error });
        }
        if (adminTemplates.error) {
          throw new Error(`Failed to load admin templates: ${adminTemplates.error.message}`, { cause: adminTemplates.error });
        }

        setTemplates([...(orgTemplates.data || [])]);
        setFilteredTemplates([...(orgTemplates.data || [])]);
        setAdminTemplates([...(adminTemplates.data || [])]);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err instanceof Error ? err : new Error('Failed to load survey templates'));
      setTemplates([]);
      setFilteredTemplates([]);
      setAdminTemplates([]);
    } finally {
      setLoading(false);
    }
  }

  const loadCampaigns = useCallback(async () => {
    if (!organizationId || isAdmin) return;
    try {
      setCampaignsLoading(true);
      const { data, error: campaignsError } = await supabase
        .from('survey_campaigns')
        .select(`
          *,
          survey_campaign_surveys(count),
          survey_invitations(respondent_id)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (campaignsError) throw campaignsError;
      setCampaigns((data as CampaignWithCounts[]) || []);
    } catch (err) {
      console.error('Error loading campaigns:', err);
    } finally {
      setCampaignsLoading(false);
    }
  }, [organizationId, isAdmin]);

  useEffect(() => {
    if (activeView === 'campaigns') {
      loadCampaigns();
    }
  }, [activeView, loadCampaigns]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (editingTemplate) {
        const { error: templateError } = await supabase
          .from('survey_templates')
          .update({
            title: formData.title,
            description: formData.description
          })
          .eq('id', editingTemplate.id);

        if (templateError) {
          throw new Error(`Failed to update template: ${templateError.message}`, { cause: templateError });
        }

        const existingQuestions = formData.questions.filter(q => q.id);
        const newQuestions = formData.questions.filter(q => !q.id);

        for (const q of existingQuestions) {
          const { error: updateError } = await supabase
            .from('survey_questions')
            .update({
              text: q.text,
              type: q.type,
              options: (q.type === 'multiple-choice' || q.type === 'multi-select') ? q.options : null,
              order_index: formData.questions.indexOf(q)
            })
            .eq('id', q.id!);

          if (updateError) {
            throw new Error(`Failed to update question: ${updateError.message}`, { cause: updateError });
          }
        }

        if (newQuestions.length > 0) {
          const { error: insertError } = await supabase
            .from('survey_questions')
            .insert(
              newQuestions.map((q, i) => ({
                template_id: editingTemplate.id,
                text: q.text,
                type: q.type,
                options: (q.type === 'multiple-choice' || q.type === 'multi-select') ? q.options : null,
                order_index: existingQuestions.length + i
              }))
            );

          if (insertError) {
            throw new Error(`Failed to add questions: ${insertError.message}`, { cause: insertError });
          }
        }

        const currentIds = new Set(formData.questions.filter(q => q.id).map(q => q.id));
        const removedIds = editingTemplate.questions
          .map(q => q.id)
          .filter(id => !currentIds.has(id));

        for (const id of removedIds) {
          const { error: deleteError } = await supabase
            .from('survey_questions')
            .delete()
            .eq('id', id);

          if (deleteError) {
            // Question likely has responses — leave it in place
          }
        }
      } else {
        const { data: templateData, error: templateError } = await supabase
          .from('survey_templates')
          .insert({
            title: formData.title,
            description: formData.description,
            status: 'draft',
            created_by: user?.id,
            organization_id: isAdmin ? null : organizationId
          })
          .select()
          .single();

        if (templateError) {
          throw new Error(`Failed to create template: ${templateError.message}`, { cause: templateError });
        }

        const { error: questionsError } = await supabase
          .from('survey_questions')
          .insert(
            formData.questions.map((q, index) => ({
              template_id: templateData.id,
              text: q.text,
              type: q.type,
              options: (q.type === 'multiple-choice' || q.type === 'multi-select') ? q.options : null,
              order_index: index
            }))
          );

        if (questionsError) {
          throw new Error(`Failed to create questions: ${questionsError.message}`, { cause: questionsError });
        }
      }

      await loadTemplates();
      setShowModal(false);
      setEditingTemplate(null);
      setFormData({
        title: '',
        description: '',
        questions: [{ text: '', type: 'multiple-choice', options: [''], order_index: 0 }]
      });
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err instanceof Error ? err : new Error('Failed to save survey template'));
    } finally {
      setSaving(false);
    }
  };

  const handleCloneTemplate = async (template: SurveyTemplateWithQuestions) => {
    try {
      const { data: newTemplate, error: templateError } = await supabase
        .from('survey_templates')
        .insert({
          title: `${template.title} (Copy)`,
          description: template.description,
          status: 'draft',
          created_by: user?.id,
          organization_id: isAdmin ? null : organizationId
        })
        .select()
        .single();

      if (templateError) {
        throw new Error(`Failed to clone template: ${templateError.message}`, { cause: templateError });
      }

      const { error: questionsError } = await supabase
        .from('survey_questions')
        .insert(
          template.questions.map(q => ({
            template_id: newTemplate.id,
            text: q.text,
            type: q.type,
            options: q.options,
            order_index: q.order_index
          }))
        );

      if (questionsError) {
        throw new Error(`Failed to clone questions: ${questionsError.message}`, { cause: questionsError });
      }

      await loadTemplates();
    } catch (err) {
      console.error('Error cloning template:', err);
      setError(err instanceof Error ? err : new Error('Failed to clone template'));
    }
  };

  const handlePublish = async (template: SurveyTemplate) => {
    try {
      const { error } = await supabase
        .from('survey_templates')
        .update({ status: 'published' })
        .eq('id', template.id);

      if (error) {
        throw new Error(`Failed to publish template: ${error.message}`, { cause: error });
      }

      await loadTemplates();
    } catch (err) {
      console.error('Error publishing template:', err);
      setError(err instanceof Error ? err : new Error('Failed to publish template'));
    }
  };

  const handleUnpublish = async (template: SurveyTemplate) => {
    try {
      const { error } = await supabase
        .from('survey_templates')
        .update({ status: 'draft' })
        .eq('id', template.id);

      if (error) {
        throw new Error(`Failed to unpublish template: ${error.message}`, { cause: error });
      }

      await loadTemplates();
    } catch (err) {
      console.error('Error unpublishing template:', err);
      setError(err instanceof Error ? err : new Error('Failed to unpublish template'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const { error } = await supabase
        .from('survey_templates')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(`Failed to delete template: ${error.message}`, { cause: error });
      }

      await loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete template'));
    }
  };

  const handleSendSurvey = async (template: SurveyTemplate) => {
    try {
      const { data: respData, error: respError } = await supabase
        .from('survey_respondents')
        .select('*')
        .eq('organization_id', template.organization_id)
        .order('last_name');

      if (respError) {
        throw new Error(`Failed to load respondents: ${respError.message}`, { cause: respError });
      }

      setRespondents(respData || []);
      setSelectedTemplate(template);
      setShowSendModal(true);
    } catch (err) {
      console.error('Error loading respondents:', err);
      setError(err instanceof Error ? err : new Error('Failed to load respondents'));
    }
  };

  const handleSendToRespondents = async (respondentIds: string[], method: 'email' | 'sms') => {
    try {
      if (!selectedTemplate) return;

      const selectedRespondentData = respondents.filter(r => respondentIds.includes(r.id));
      const successfulSends: string[] = [];
      const failedSends: string[] = [];

      // Send emails sequentially to avoid rate limits
      for (const respondent of selectedRespondentData) {
        try {
          // First, expire any existing invitations for this respondent and survey
          const { error: updateError } = await supabase
            .from('survey_invitations')
            .update({
              status: 'expired',
              expires_at: new Date().toISOString()
            })
            .match({
              survey_id: selectedTemplate.id,
              respondent_id: respondent.id,
              status: 'sent'
            });

          if (updateError) {
            console.error('Error expiring old invitation:', updateError);
          }

          // Create new invitation
          const expiresAt = new Date();
          expiresAt.setDate(expiresAt.getDate() + 7); // 7 days from now

          const { error: inviteError } = await supabase
            .from('survey_invitations')
            .insert({
              survey_id: selectedTemplate.id,
              respondent_id: respondent.id,
              status: 'sent',
              sent_at: new Date().toISOString(),
              expires_at: expiresAt.toISOString()
            });

          if (inviteError) {
            console.error('Error creating invitation:', inviteError);
            failedSends.push(respondent.email);
            continue;
          }

          if (method === 'email') {
            const surveyLink = `${window.location.origin}/surveys/${selectedTemplate.id}/respond/${respondent.id}`;
            
            await sendSurveyEmail({
              to: respondent.email,
              recipientName: `${respondent.first_name} ${respondent.last_name}`,
              surveyTitle: selectedTemplate.title,
              surveyLink
            });

            successfulSends.push(respondent.email);
          }
        } catch (err) {
          console.error(`Failed to send email to ${respondent.email}:`, err);
          failedSends.push(respondent.email);
        }
      }

      // Show appropriate success/error message
      if (failedSends.length === 0) {
        setSuccess(`Survey invitations sent successfully to ${successfulSends.length} recipient(s)`);
      } else if (successfulSends.length === 0) {
        setError(new Error('Failed to send all invitations. Please try again.'));
      } else {
        setSuccess(`Successfully sent ${successfulSends.length} invitation(s). Failed to send to ${failedSends.length} recipient(s).`);
      }

      setShowSendModal(false);
      setSelectedTemplate(null);
    } catch (err) {
      console.error('Error sending survey:', err);
      setError(err instanceof Error ? err : new Error('Failed to send survey invitations'));
    }
  };

  const handleOpenCampaignModal = async () => {
    try {
      if (!organizationId) return;
      const { data, error: respError } = await supabase
        .from('survey_respondents')
        .select('*')
        .eq('organization_id', organizationId)
        .order('last_name');

      if (respError) throw new Error(respError.message);
      setCampaignRespondents(data || []);
      setShowCampaignModal(true);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load respondents'));
    }
  };

  const handleSendCampaign = async (params: {
    name: string;
    description: string;
    surveyIds: string[];
    respondentIds: string[];
  }) => {
    if (!organizationId || !user?.id) return;

    const { data: campaign, error: campaignError } = await supabase
      .from('survey_campaigns')
      .insert({
        organization_id: organizationId,
        name: params.name,
        description: params.description || null,
        status: 'sending',
        created_by: user.id
      })
      .select()
      .single();

    if (campaignError) throw new Error(`Failed to create campaign: ${campaignError.message}`);

    const { error: surveysError } = await supabase
      .from('survey_campaign_surveys')
      .insert(
        params.surveyIds.map((surveyId, index) => ({
          campaign_id: campaign.id,
          survey_id: surveyId,
          order_index: index
        }))
      );

    if (surveysError) throw new Error(`Failed to link surveys: ${surveysError.message}`);

    const selectedRespondentData = campaignRespondents.filter(r =>
      params.respondentIds.includes(r.id)
    );

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const successfulSends: string[] = [];
    const failedSends: string[] = [];

    for (const respondent of selectedRespondentData) {
      try {
        const invitations = params.surveyIds.map(surveyId => ({
          survey_id: surveyId,
          respondent_id: respondent.id,
          campaign_id: campaign.id,
          status: 'sent' as const,
          sent_at: new Date().toISOString(),
          expires_at: expiresAt.toISOString()
        }));

        const { error: inviteError } = await supabase
          .from('survey_invitations')
          .insert(invitations);

        if (inviteError) {
          failedSends.push(respondent.email);
          continue;
        }

        const surveyTitles = params.surveyIds
          .map(id => [...templates, ...adminTemplates].find(t => t.id === id)?.title)
          .filter(Boolean) as string[];

        const campaignLink = `${window.location.origin}/campaigns/${campaign.id}/respond/${respondent.id}`;

        await sendCampaignEmail({
          to: respondent.email,
          recipientName: `${respondent.first_name} ${respondent.last_name}`,
          campaignName: params.name,
          surveyTitles,
          campaignLink
        });

        successfulSends.push(respondent.email);
      } catch (err) {
        failedSends.push(respondent.email);
      }
    }

    await supabase
      .from('survey_campaigns')
      .update({ status: 'sent' })
      .eq('id', campaign.id);

    if (failedSends.length === 0) {
      setSuccess(`Campaign sent successfully to ${successfulSends.length} recipient(s)`);
    } else if (successfulSends.length === 0) {
      throw new Error('Failed to send campaign to all recipients. Please try again.');
    } else {
      setSuccess(`Campaign sent to ${successfulSends.length} recipient(s). Failed: ${failedSends.length}.`);
    }

    setShowCampaignModal(false);
    setActiveView('campaigns');
    await loadCampaigns();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center">
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
              {isAdmin
                ? 'Admin Survey Templates'
                : activeView === 'templates'
                  ? 'Your Survey Templates'
                  : 'Your Campaigns'}
            </h1>
          </div>
          <div className="flex gap-2">
            {activeView === 'templates' ? (
              <Button onClick={() => setShowModal(true)}>
                <Plus className="h-5 w-5 mr-2" />
                {isAdmin ? 'Create Template' : 'Create Survey'}
              </Button>
            ) : (
              <Button onClick={handleOpenCampaignModal}>
                <Plus className="h-5 w-5 mr-2" />
                New Campaign
              </Button>
            )}
          </div>
        </div>

        {/* View Toggle */}
        {!isAdmin && (
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveView('templates')}
              className={cn(
                "inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors",
                activeView === 'templates'
                  ? "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              )}
            >
              Survey Templates
            </button>
            <button
              onClick={() => setActiveView('campaigns')}
              className={cn(
                "inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors",
                activeView === 'campaigns'
                  ? "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              )}
            >
              Campaigns
            </button>
          </div>
        )}

        {error && (
          <ErrorDisplay 
            error={error}
            context="Survey Management"
            onRetry={activeView === 'templates' ? loadTemplates : loadCampaigns}
            className="mb-4"
          />
        )}

        {success && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Templates View */}
        {activeView === 'templates' && (
          <div>
            <SurveyFilters 
              onFilterChange={handleFilterChange}
              currentFilter={statusFilter}
            />

            <SurveyList
              templates={filteredTemplates}
              onEdit={(template) => {
                setEditingTemplate(template);
                setFormData({
                  title: template.title,
                  description: template.description || '',
                  questions: template.questions.map(q => ({
                    id: q.id,
                    text: q.text,
                    type: q.type,
                    options: q.options || [''],
                    order_index: q.order_index
                  }))
                });
                setShowModal(true);
              }}
              onDuplicate={handleCloneTemplate}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              onDelete={handleDelete}
              onSend={!isAdmin ? handleSendSurvey : undefined}
            />
          </div>
        )}

        {/* Campaigns View */}
        {activeView === 'campaigns' && (
          campaignsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
            </div>
          ) : (
            <CampaignList campaigns={campaigns} />
          )
        )}

        {/* Available Templates Section */}
        {activeView === 'templates' && !isAdmin && adminTemplates.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center mb-6">
              <ClipboardList className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Available Templates
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {adminTemplates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 flex flex-col"
                >
                  <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">
                    {template.title}
                  </h3>
                  {template.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4 flex-grow">
                      {template.description}
                    </p>
                  )}
                  <div className="space-y-2">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {template.questions.length} questions
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setPreviewTemplate(template)}
                        className="w-full justify-center"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleCloneTemplate(template)}
                        className="w-full justify-center"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Use Template
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form Modal */}
        <SurveyFormModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingTemplate(null);
            setFormData({
              title: '',
              description: '',
              questions: [{ text: '', type: 'multiple-choice', options: [''], order_index: 0 }]
            });
          }}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
          editingTemplate={editingTemplate}
          saving={saving}
        />

        {/* Send Survey Modal */}
        {showSendModal && selectedTemplate && (
          <SendSurveyModal
            isOpen={showSendModal}
            onClose={() => {
              setShowSendModal(false);
              setSelectedTemplate(null);
            }}
            template={selectedTemplate}
            respondents={respondents}
            onSend={handleSendToRespondents}
          />
        )}

        {/* Campaign Modal */}
        {showCampaignModal && (
          <CreateCampaignModal
            isOpen={showCampaignModal}
            onClose={() => setShowCampaignModal(false)}
            templates={[...templates, ...adminTemplates]}
            respondents={campaignRespondents}
            onSend={handleSendCampaign}
          />
        )}

        {/* Preview Modal */}
        <SurveyPreviewModal
          isOpen={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          template={previewTemplate || { title: '', description: '', questions: [] }}
        />
      </div>
    </Layout>
  );
}